import { cssObj } from '@/css/resources/functions.css';
import {
	breakpoints,
	fontWeights,
	containers,
	gridLayout,
} from '@/css/resources/variables.css';

/*********** Resets ***********/
/*
 * Button
 */
export const resetButton = cssObj({
	backgroundColor: 'transparent',
	border: 0,
	borderRadius: 0,
	outline: 0,
	appearance: 'none',
	padding: 0,
	cursor: 'pointer',

	'&::-moz-focus-inner': {
		border: 0,
	},
});

/*
 * Input
 */
export const resetInput = cssObj({
	fontSize: '1em',
	backgroundColor: 'transparent',
	border: 0,
	borderRadius: 0,
	outline: 0,
	padding: 0,
	appearance: 'none',
});

/*
 * Remove margin-top if element is a first-child and margin-bottom if element is a last-child
 */
export const resetFirstMarginTop = cssObj({
	'&:first-child': {
		marginTop: '0px',
	},
});
export const resetLastMarginBottom = cssObj({
	'&:last-child': {
		marginBottom: '0px',
	},
});
export const resetFirstLastMargins = cssObj(
	resetFirstMarginTop,
	resetLastMarginBottom
);

/*
 * Remove margin-top from first-child and margin-bottom from last-child
 */
export const resetFirstChildMarginTop = cssObj({
	'& > *:first-child': {
		marginTop: '0px !important',
	},
});
export const resetLastChildMarginBottom = cssObj({
	'& > *:last-child': {
		marginBottom: '0px !important',
	},
});
export const resetFirstLastChildMargins = cssObj(
	resetFirstChildMarginTop,
	resetLastChildMarginBottom
);

/*********** GRID ***********/
/**
 * Base
 */
export const container = cssObj({
	marginLeft: containers.minOffset,
	marginRight: containers.minOffset,

	[`@media (min-width: ${breakpoints.tablet})`]: {
		marginLeft: containers.tabletOffset,
		marginRight: containers.tabletOffset,
	},

	[`@media (min-width: ${breakpoints.laptop})`]: {
		marginLeft: containers.laptopOffset,
		marginRight: containers.laptopOffset,
	},

	[`@media (min-width: ${breakpoints.desktop})`]: {
		marginLeft: containers.desktopOffset,
		marginRight: containers.desktopOffset,
	},

	[`@media (min-width: ${containers.maxWidthMq})`]: {
		width: '100%',
		maxWidth: containers.maxWidth,
		marginLeft: 'auto',
		marginRight: 'auto',
	},
});
/**
 * Gaps
 */
export const gridColumnGap = cssObj({
	gridColumnGap: gridLayout.gap,

	[`@media (min-width: ${gridLayout.gapMaxMq})`]: {
		gridColumnGap: gridLayout.gapMax,
	},
});

export const gridRowGap = cssObj({
	gridRowGap: gridLayout.gap,

	[`@media (min-width: ${gridLayout.gapMaxMq})`]: {
		gridRowGap: gridLayout.gapMax,
	},
});

export const grid = cssObj(container, gridColumnGap, {
	display: 'grid',

	/* grid layout and behaviour */
	gridTemplateColumns:
		'[grid-start] 1fr [content-start] 1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr [content-end] 1fr [grid-end]',

	gridAutoRows: 'auto',
	gridTemplateRows: 'auto',
});

export const gridNoMargin = cssObj(gridColumnGap, {
	display: 'grid',

	/* grid layout and behaviour */
	gridTemplateColumns:
		'[grid-start] 1fr [content-start] 1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr [content-end] 1fr [grid-end]',

	gridAutoRows: 'auto',
	gridTemplateRows: 'auto',
});

/*
 * Grid Column Start/End
 */
export const gridFull = cssObj({
	gridColumn: '1/13',
});

/*
 * Grid content default
 */
export const gridContentDefault = cssObj(gridFull, {
	[`@media (min-width: ${breakpoints.tablet})`]: {
		gridColumn: '2/12',
	},
	[`@media (min-width: ${breakpoints.bigLaptop})`]: {
		gridColumn: '3/11',
	},
});

/*********** List ***********/
/*
 * List Markers
 */
export const ulMarker = cssObj({
	top: '0.2em',
	width: '12px',
	height: '12px',
	borderTop: '6px solid transparent',
	borderRight: '6px solid transparent',
	borderBottom: '6px solid transparent',
	boxSizing: 'border-box',
});

export const olMarker = cssObj({
	counterIncrement: 'li',
	top: '0em',
	fontWeight: fontWeights.bold,
});

/*********** Styles ***********/
export const linkUnderline = cssObj({
	/* text-decoration: underline; */
	textDecoration: 'none',
	background: 'linear-gradient(currentColor, currentColor) no-repeat',
	backgroundSize: '100% 1px',
	backgroundRepeat: 'no-repeat',
	backgroundPosition: '0 100%',
	/* text-shadow: 0.05em 0 white, -0.05em 0 white; */
});

export const textDefaultStyles = cssObj({
	'& > b, & > strong': {
		fontWeight: fontWeights.bold,
	},

	'& > em, & > i': {
		fontStyle: 'italic',
	},

	'& > a': linkUnderline,
});

export const linkFooter = cssObj({
	position: 'relative',
	paddingBottom: '2px',
	whiteSpace: 'nowrap',

	'&::after': {
		content: '""',
		position: 'absolute',
		left: '0',
		bottom: '0',
		width: '100%',
		height: '1px',
		opacity: '0',
		transition: 'opacity 0.2s ease-out',
	},

	'&:hover::after': {
		opacity: '1',
	},
});

/*********** Others ***********/
export const clearfix = cssObj({
	'&::after': {
		content: '""',
		display: 'table',
		clear: 'both',
	},
});

export const outlineA11y = cssObj({
	outlineColor: '-webkit-focus-ring-color',
	outlineOffset: '0px',
	outlineStyle: 'auto',
	outlineWidth: '5px',
});
